<div class="container mx-auto space-y-6">
  <!-- Page Header -->
  <div
    class="flex flex-wrap items-center justify-between pb-4 border-b border-base-300"
  >
    <div>
      <h1 class="text-2xl font-bold text-base-content">
        Earnings & Withdrawals
      </h1>
      <p class="text-sm text-base-content/60 mt-1">
        Track your income and request payouts
      </p>
    </div>
    <button
      class="daisy-btn daisy-btn-primary"
      (click)="openWithdrawalForm()"
      [disabled]="
        !ledger() || (ledger()?.availableForWithdrawal ?? 0) < MIN_WITHDRAWAL
      "
    >
      <i class="fa-solid fa-money-bill-transfer mr-2"></i>
      Withdraw
    </button>
  </div>

  <!-- Academy Admin Notice -->
  @if (isAcademyAdmin()) {
  <div class="daisy-alert daisy-alert-info">
    <i class="fa-solid fa-building-columns"></i>
    <div class="text-sm">
      <span class="font-semibold">Academy Treasury & Escrow:</span>
      This dashboard reflects personal instructor earnings. As an Academy Administrator, you can view your organization's escrow ledger, revenue, and request payouts in the
      <a routerLink="/admin/earnings" class="daisy-link font-bold underline ml-1">Admin Treasury</a>.
    </div>
  </div>
  }

  <!-- Success message -->
  @if (withdrawalSuccess()) {
  <div class="daisy-alert daisy-alert-success">
    <i class="fa-solid fa-circle-check"></i>
    <span
      >Withdrawal request submitted successfully! We will process it
      shortly.</span
    >
  </div>
  }

  <!-- Direct Settlement Notice if tutor uses direct payment gateway -->
  @if ((ledger()?.directSettledBalance ?? 0) > 0) {
  <div class="daisy-alert daisy-alert-info">
    <i class="fa-solid fa-circle-info"></i>
    <span class="text-sm">
      <strong>Direct Gateway Settlement:</strong> You have <strong>LKR {{ (ledger()?.directSettledBalance ?? 0) | number: '1.2-2' }}</strong> in direct gateway earnings deposited directly into your configured payment gateway or bank account. Platform withdrawal requests are not required for direct settlements.
    </span>
  </div>
  }

  <!-- Ledger Cards -->
  @if (ledger()) {
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
    <!-- Pending Balance (in grace) -->
    <div
      class="daisy-stat bg-base-100 rounded-xl shadow border border-base-200"
    >
      <div class="daisy-stat-figure text-warning">
        <i class="fa-solid fa-hourglass-half text-2xl"></i>
      </div>
      <div class="daisy-stat-title text-xs">Pending Balance</div>
      <div class="daisy-stat-value text-warning text-xl">
        LKR {{ ledger()?.pendingBalance | number: '1.2-2' }}
      </div>
      <div class="daisy-stat-desc">In 7-day grace period</div>
    </div>

    <!-- Available for Withdrawal -->
    <div
      class="daisy-stat bg-base-100 rounded-xl shadow border border-base-200"
    >
      <div class="daisy-stat-figure text-success">
        <i class="fa-solid fa-circle-check text-2xl"></i>
      </div>
      <div class="daisy-stat-title text-xs">Available for Withdrawal</div>
      <div class="daisy-stat-value text-success text-xl">
        LKR {{ ledger()?.availableForWithdrawal | number: '1.2-2' }}
      </div>
      <div class="daisy-stat-desc">
        Min. LKR {{ MIN_WITHDRAWAL | number }} to withdraw
      </div>
    </div>

    <!-- Total Earned (net) -->
    <div
      class="daisy-stat bg-base-100 rounded-xl shadow border border-base-200"
    >
      <div class="daisy-stat-figure text-primary">
        <i class="fa-solid fa-coins text-2xl"></i>
      </div>
      <div class="daisy-stat-title text-xs">Total Earned (Net)</div>
      <div class="daisy-stat-value text-primary text-xl">
        LKR {{ ledger()?.totalEarned | number: '1.2-2' }}
      </div>
      <div class="daisy-stat-desc">After fee deductions</div>
    </div>

    <!-- Direct Settled / Platform Fees -->
    @if ((ledger()?.directSettledBalance ?? 0) > 0) {
    <div
      class="daisy-stat bg-base-100 rounded-xl shadow border border-base-200"
    >
      <div class="daisy-stat-figure text-info">
        <i class="fa-solid fa-building-columns text-2xl"></i>
      </div>
      <div class="daisy-stat-title text-xs">Direct Settled</div>
      <div class="daisy-stat-value text-info text-xl">
        LKR {{ ledger()?.directSettledBalance | number: '1.2-2' }}
      </div>
      <div class="daisy-stat-desc">Settled to your gateway</div>
    </div>
    } @else {
    <div
      class="daisy-stat bg-base-100 rounded-xl shadow border border-base-200"
    >
      <div class="daisy-stat-figure text-base-content/40">
        <i class="fa-solid fa-percent text-2xl"></i>
      </div>
      <div class="daisy-stat-title text-xs">Total Service Fees</div>
      <div class="daisy-stat-value text-base-content/60 text-xl">
        LKR {{ ledger()?.totalServiceFees | number: '1.2-2' }}
      </div>
      <div class="daisy-stat-desc">{{ feePercent() }} commission</div>
    </div>
    }
  </div>
  } @else {
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
    @for (i of [1,2,3,4]; track i) {
    <div class="daisy-skeleton h-24 rounded-xl"></div>
    }
  </div>
  }

  <!-- Plan Info Card -->
  <div class="bg-base-100 rounded-xl shadow border border-base-200 p-5">
    <div class="flex items-center justify-between mb-3">
      <h2 class="font-semibold text-base-content">
        <i class="fa-solid fa-layer-group mr-2"></i>Your Plan
      </h2>
    </div>

    @if (activePlan()) {
    <div class="flex flex-wrap justify-between">
      <div>
        <span class="text-xs text-base-content/50 uppercase tracking-wide"
          >Plan</span
        >
        <p class="font-semibold text-base-content">{{ activePlan()?.name }}</p>
        @if (activePlan()?.description) {
        <p class="text-xs text-base-content/50 mt-0.5">
          {{ activePlan()?.description }}
        </p>
        }
      </div>
      <div>
        <span class="text-xs text-base-content/50 uppercase tracking-wide"
          >Commission Rate</span
        >
        <p class="font-semibold text-base-content">{{ feePercent() }}</p>
      </div>
      <div>
        <span class="text-xs text-base-content/50 uppercase tracking-wide"
          >Max Live Classes</span
        >
        <p class="font-semibold text-base-content">
          {{ activePlan()?.quotas?.['maxLiveClasses'] === -1 ? 'Unlimited' : activePlan()?.quotas?.['maxLiveClasses'] }}
        </p>
      </div>
      <div>
        <span class="text-xs text-base-content/50 uppercase tracking-wide"
          >Max Active Students</span
        >
        <p class="font-semibold text-base-content">
          {{ activePlan()?.quotas?.['maxActiveStudents'] === -1 ? 'Unlimited' : activePlan()?.quotas?.['maxActiveStudents'] }}
        </p>
      </div>
    </div>
    <div class="mt-4 pt-4 border-t border-base-200">
      <div class="daisy-alert daisy-alert-info daisy-alert-sm">
        <i class="fa-solid fa-circle-info flex-shrink-0"></i>
        <span class="text-sm"
          >To upgrade or change your plan, please
          <strong>contact our admin team</strong>. Plan changes are handled
          manually to ensure a smooth transition.</span
        >
      </div>
    </div>
    } @else if (isSoloTutor()) {
    <p class="text-sm text-base-content/50">
      Standard Solo Tutor plan. Custom platform rates apply.
    </p>
    } @else {
    <p class="text-sm text-base-content/50">
      No active plan assigned. Please contact admin.
    </p>
    }
  </div>

  <!-- Bank Account Section -->
  <div class="bg-base-100 rounded-xl shadow border border-base-200 p-5">
    <div class="flex items-center justify-between mb-3">
      <h2 class="font-semibold text-base-content">
        <i class="fa-solid fa-building-columns mr-2"></i>Bank Account
      </h2>
      <button
        class="daisy-btn daisy-btn-ghost daisy-btn-sm"
        (click)="openBankAccountForm()"
      >
        @if (bankAccount()?._id) { Edit } @else { Add Account }
      </button>
    </div>

    @if (bankAccount()?._id) {
    <div class="grid grid-cols-2 gap-3 text-sm">
      <div>
        <span class="text-base-content/50">Account Holder</span>
        <p class="font-medium">{{ bankAccount()?.accountHolderName }}</p>
      </div>
      <div>
        <span class="text-base-content/50">Bank</span>
        <p class="font-medium">{{ bankAccount()?.bankName }}</p>
      </div>
      <div>
        <span class="text-base-content/50">Branch</span>
        <p class="font-medium">{{ bankAccount()?.branchName }}</p>
      </div>
      <div>
        <span class="text-base-content/50">Account Number</span>
        <p class="font-medium">{{ bankAccount()?.accountNumber }}</p>
      </div>
    </div>

    @if (bankBookImageUrl()) {
    <div class="mt-4 pt-4 border-t border-base-200 flex items-center gap-3">
      <i class="fa-solid fa-paperclip text-base-content/40"></i>
      <span class="text-sm text-base-content/60">Bank Book Front Page</span>

      @if (bankBookIsImage()) {
      <!-- Image: show thumbnail that opens a popup -->
      <button
        type="button"
        class="daisy-btn daisy-btn-ghost daisy-btn-xs gap-1"
        (click)="openBankBookPreview()"
      >
        <i class="fa-solid fa-eye"></i>
        View
      </button>
      } @else {
      <!-- Non-image (PDF etc.): download link -->
      <a
        [href]="bankBookImageUrl()"
        target="_blank"
        rel="noopener noreferrer"
        class="daisy-btn daisy-btn-ghost daisy-btn-xs gap-1"
        download
      >
        <i class="fa-solid fa-download"></i>
        Download
      </a>
      }
    </div>
    } } @else {
    <p class="text-sm text-base-content/50">
      No bank account saved. Add one to enable withdrawals.
    </p>
    }
  </div>

  <!-- Earnings List -->
  <div class="bg-base-100 rounded-xl shadow border border-base-200">
    <div class="p-5 border-b border-base-200">
      <h2 class="font-semibold text-base-content">
        <i class="fa-solid fa-list mr-2"></i>Earnings History
      </h2>
    </div>
    <div class="overflow-x-auto">
      <table class="daisy-table daisy-table-sm w-full">
        <thead>
          <tr class="bg-base-200/50">
            <th>Date</th>
            <th>Gross Amount</th>
            <th>Fee Deductions ({{ feePercent() }})</th>
            <th>Net Amount</th>
            <th>Status</th>
            <th>Grace Period Ends</th>
          </tr>
        </thead>
        <tbody>
          @if (earnings().length === 0) {
          <tr>
            <td colspan="6" class="text-center text-base-content/50 py-8">
              No earnings yet.
            </td>
          </tr>
          } @for (earning of earnings(); track earning._id) {
          <tr class="hover:bg-base-200/30">
            <td>{{ earning.createdAt | date: 'mediumDate' }}</td>
            <td>LKR {{ earning.grossAmount | number: '1.2-2' }}</td>
            <td class="text-error">
              - LKR {{ earning.platformFee | number: '1.2-2' }}
            </td>
            <td class="font-medium text-success">
              LKR {{ earning.netAmount | number: '1.2-2' }}
            </td>
            <td>
              <span
                class="daisy-badge daisy-badge-sm {{
                  getStatusBadgeClass(earning.status)
                }}"
              >
                {{ earning.status | titlecase }}
              </span>
            </td>
            <td>
              @if (earning.status === 'PENDING_GRACE') {
              {{ earning.gracePeriodEndsOn | date: 'mediumDate' }}
              } @else if (earning.status === 'SETTLED_DIRECTLY') {
              <span class="text-xs text-info font-medium">Direct Settlement</span>
              } @else { — }
            </td>
          </tr>
          }
        </tbody>
      </table>
    </div>
  </div>

  <!-- Withdrawal History -->
  <div class="bg-base-100 rounded-xl shadow border border-base-200">
    <div class="p-5 border-b border-base-200">
      <h2 class="font-semibold text-base-content">
        <i class="fa-solid fa-clock-rotate-left mr-2"></i>Withdrawal History
      </h2>
    </div>
    <div class="overflow-x-auto">
      <table class="daisy-table daisy-table-sm w-full">
        <thead>
          <tr class="bg-base-200/50">
            <th>Requested On</th>
            <th>Amount</th>
            <th>Bank Account</th>
            <th>Status</th>
            <th>Processed On</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          @if (withdrawals().length === 0) {
          <tr>
            <td colspan="6" class="text-center text-base-content/50 py-8">
              No withdrawal requests yet.
            </td>
          </tr>
          } @for (withdrawal of withdrawals(); track withdrawal._id) {
          <tr class="hover:bg-base-200/30">
            <td>{{ withdrawal.requestedOn | date: 'medium' }}</td>
            <td class="font-medium">
              LKR {{ withdrawal.amount | number: '1.2-2' }}
            </td>
            <td class="text-sm">
              {{ withdrawal.bankAccount.bankName }} —
              {{ withdrawal.bankAccount.accountNumber }}
            </td>
            <td>
              <span
                class="daisy-badge daisy-badge-sm {{
                  getWithdrawalStatusClass(withdrawal.status)
                }}"
              >
                {{ withdrawal.status | titlecase }}
              </span>
            </td>
            <td>
              {{
                withdrawal.processedOn
                  ? (withdrawal.processedOn | date: 'mediumDate')
                  : '—'
              }}
            </td>
            <td>{{ withdrawal.adminNote ?? '—' }}</td>
          </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
</div>

<!-- Withdrawal Request Modal -->
@if (showWithdrawalForm()) {
<div class="daisy-modal daisy-modal-open">
  <div class="daisy-modal-box">
    <h3 class="font-bold text-lg mb-4">Request Withdrawal</h3>

    <div class="space-y-4">
      @if (withdrawalError()) {
      <div class="daisy-alert daisy-alert-error daisy-alert-sm">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <span>{{ withdrawalError() }}</span>
      </div>
      }

      <div class="bg-base-200 rounded-lg p-4 text-center space-y-1">
        <span class="text-xs text-base-content/60 uppercase font-semibold tracking-wider">Withdrawal Amount</span>
        <div class="text-3xl font-extrabold text-primary">
          LKR {{ (ledger()?.availableForWithdrawal ?? 0) | number: '1.2-2' }}
        </div>
        <p class="text-xs text-base-content/70 pt-1">
          <i class="fa-solid fa-circle-info text-info mr-1"></i>
          This full amount will be withdrawn to your bank account.
        </p>
      </div>

      @if ((ledger()?.availableForWithdrawal ?? 0) < MIN_WITHDRAWAL) {
      <div class="daisy-alert daisy-alert-warning daisy-alert-sm">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <span>Minimum withdrawal balance required is LKR {{ MIN_WITHDRAWAL | number }}.</span>
      </div>
      }

      <div class="text-sm text-base-content/60">
        <p>
          <i class="fa-solid fa-building-columns mr-1"></i>
          Transfer will be sent to:
          <strong
            >{{ bankAccount()?.bankName }} —
            {{ bankAccount()?.accountNumber }}</strong
          >
        </p>
      </div>
    </div>

    <div class="daisy-modal-action">
      <button class="daisy-btn daisy-btn-ghost" (click)="closeWithdrawalForm()">
        Cancel
      </button>
      <button
        class="daisy-btn daisy-btn-primary"
        (click)="submitWithdrawal()"
        [disabled]="(ledger()?.availableForWithdrawal ?? 0) < MIN_WITHDRAWAL || !bankAccount()?._id"
      >
        Confirm Withdrawal
      </button>
    </div>
  </div>
  <div class="daisy-modal-backdrop" (click)="closeWithdrawalForm()"></div>
</div>
}

<!-- Bank Account Form Modal -->
@if (showBankAccountForm()) {
<div class="daisy-modal daisy-modal-open">
  <div class="daisy-modal-box">
    <black-bank-account-form
      [currentAccount]="bankAccount()"
      (saved)="saveBankAccount($event)"
      (cancelled)="closeBankAccountForm()"
    ></black-bank-account-form>
  </div>
  <div class="daisy-modal-backdrop" (click)="closeBankAccountForm()"></div>
</div>
}

<!-- Bank Book Image Preview Modal -->
@if (showBankBookPreview() && bankBookImageUrl()) {
<div class="daisy-modal daisy-modal-open">
  <div class="daisy-modal-box max-w-2xl">
    <div class="flex items-center justify-between mb-4">
      <h3 class="font-bold text-lg">Bank Book Front Page</h3>
      <button
        type="button"
        class="daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-circle"
        (click)="closeBankBookPreview()"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
    <img
      [src]="bankBookImageUrl()"
      alt="Bank book front page"
      class="w-full rounded-lg border border-base-200 object-contain max-h-[70vh]"
    />
  </div>
  <div class="daisy-modal-backdrop" (click)="closeBankBookPreview()"></div>
</div>
}
